import { asMarkdownTemplateResult } from '../../../../webmodule-common/other/general/markdown';
import { awaitableTemplate } from '../../../../webmodule-common/other/ui/template-processor';
import { cache } from '../../cache-impl/cache-registry';
import { html } from 'lit';
import { isEmptyOrSpace } from '../../../../webmodule-common/other/ui/string-helper-functions';
import { LangFranchiseeQuoteManager } from '../../../../localization/scripts/dealer-franchisee/quotes/data/franchisee-quote-manager-lang';

import type { FranchiseeQuoteContainerManager } from '../data/franchisee-quote-manager';
import type { TemplateResult } from 'lit';

/**
 * The pricing-model tooltip, built from the manager's state.
 *
 * This used to be a method ON FranchiseeQuoteContainerManager, which meant a data/persistence class
 * imported lit, the template processor and the markdown renderer purely to build a tooltip. All
 * three call sites were already in the view layer, so the template belongs here.
 */
export function pricingModelTooltip(qcm: FranchiseeQuoteContainerManager): TemplateResult {
  if (qcm.isPricingMetricLockedBySupplier) {
    return asMarkdownTemplateResult(LangFranchiseeQuoteManager.pricingIsSetByTheSupplier());
  }

  const defaultPercValue = (async () => {
    const paymentProfile = await cache().paymentProfile.get(qcm.branchQuote?.clientTypeId);
    return paymentProfile?.data.paymentProfile.value ?? qcm.quotePrice.percentMarginOrMarkup;
  })();
  const constraint = qcm.pricingModel.pricingModelContstraintText;

  const defaultTxt = async () => {
    const awaitedValue = await defaultPercValue;
    return asMarkdownTemplateResult(
      LangFranchiseeQuoteManager.pricingModelTooltipDefault({
        calculationLabel: qcm.pricingModel.calculationLabel,
        value: awaitedValue
      })
    );
  };
  const quoteOverrride = async () => {
    const value = await defaultPercValue;
    return value !== qcm.quotePrice.percentMarginOrMarkup
      ? asMarkdownTemplateResult(
          LangFranchiseeQuoteManager.pricingModelTooltipOverride({
            value: qcm.quotePrice.percentMarginOrMarkup
          })
        )
      : html`<br />`;
  };
  const supplierConstraint = isEmptyOrSpace(constraint)
    ? ''
    : asMarkdownTemplateResult(
        LangFranchiseeQuoteManager.pricingModelTooltipSupplierConstrained({
          supplierName: qcm.supplierName,
          constraint
        })
      );
  return html`${awaitableTemplate(defaultTxt)}${awaitableTemplate(quoteOverrride)}${supplierConstraint}`;
}
